import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, Gift, Share2, UserPlus } from "lucide-react";
import { useReferralCta } from "../../hooks/useReferralCta";

// Deep green, deliberately darker than the brand green (#22c55e) so the ribbon reads
// as a colour break between the hero and the white Services block without competing
// with the primary CTAs sitting just above it in the carousel.
const RIBBON_BG = "#0f5132";
const MINT = "#7ee0a4";

// The ribbon bridges two very different neighbours: the hero bottoms out at
// rgba(10,16,14,0.82) and Services opens on white. A flat fill made both seams read as
// hard bands, so the fill starts at the hero's near-black green and lifts toward a
// brighter green at the bottom, where a mint glow and hairline hand off to the white.
const RIBBON_GRADIENT =
  "linear-gradient(180deg, #0a140f 0%, #0c3520 38%, #0f5132 74%, #126038 100%)";

export default function HomeReferralRibbon() {
  const navigate = useNavigate();
  // The home page is public, so a signed-out visitor has no code to share. `isGuest`
  // is gated on auth loading so a returning member never sees "Register now" flash.
  const {
    isGuest,
    loading,
    referralCode,
    copied,
    copyFailed,
    copyCode: handleCopyCode,
    shareInvite: handleShare,
  } = useReferralCta();

  const message = isGuest
    ? "Register, grab your code, and earn a free hour when a friend completes their first clean."
    : "Your referral code is ready — share it and earn a free hour for every friend.";

  return (
    <Box
      as="section"
      aria-label="Referral programme"
      w="100vw"
      position="relative"
      left="50%"
      ml="-50vw"
      bg={RIBBON_GRADIENT}
      boxShadow={`inset 0 -1px 0 ${MINT}47`}
      overflow="hidden"
    >
      {/* Faint dot field, echoing the dashboard referral banner's dark panel. Masked so
          it fades out at both edges instead of colliding with the hero and Services. */}
      <Box
        position="absolute"
        inset={0}
        pointerEvents="none"
        backgroundImage="radial-gradient(circle, rgba(126,224,164,0.13) 1.5px, transparent 1.5px)"
        backgroundSize="28px 28px"
        maskImage="linear-gradient(180deg, transparent 0%, #000 45%, #000 70%, transparent 100%)"
        css={{
          WebkitMaskImage:
            "linear-gradient(180deg, transparent 0%, #000 45%, #000 70%, transparent 100%)",
        }}
      />

      {/* Bottom-centre mint glow — echoes the green radials ServiceHighlights opens
          with, so the ribbon lifts into the white block instead of stopping dead. */}
      <Box
        position="absolute"
        bottom="-7rem"
        left="50%"
        transform="translateX(-50%)"
        w="min(52rem, 92%)"
        h="12rem"
        rounded="full"
        bg={`${MINT}2b`}
        filter="blur(64px)"
        pointerEvents="none"
      />

      <Flex
        position="relative"
        zIndex={1}
        maxW="1280px"
        mx="auto"
        px={{ base: "1rem", tablet: "1.5rem", desktop: "2rem" }}
        py={{ base: "1.1rem", tablet: "1.15rem" }}
        direction={{ base: "column", tablet: "row" }}
        align={{ base: "stretch", tablet: "center" }}
        gap={{ base: "0.85rem", tablet: "1.75rem" }}
      >
        <Flex
          align="center"
          gap="0.35rem"
          alignSelf="center"
          flexShrink={0}
          rounded="full"
          border={`1px solid ${MINT}66`}
          color={MINT}
          px="0.75rem"
          py="0.3rem"
        >
          <Gift size={12} />
          <Text
            fontSize="0.66rem"
            fontWeight="800"
            letterSpacing="0.14em"
            textTransform="uppercase"
            lineHeight="1"
          >
            Referrals
          </Text>
        </Flex>

        <Text
          flex="1"
          minW={0}
          color="white"
          fontWeight="600"
          fontSize={{ base: "0.86rem", tablet: "0.95rem" }}
          lineHeight="1.5"
          textAlign={{ base: "center", tablet: "left" }}
          textWrap="pretty"
        >
          {message}
        </Text>

        {/* Mirrors the dashboard banner's CTA logic: guests get a signup nudge,
            members get copy-code + share-link, disabled until the code arrives. */}
        {loading ? (
          // Reserve the CTA footprint while auth resolves so the ribbon doesn't jump.
          <Box visibility="hidden" h={{ base: "2.6rem", tablet: "2.55rem" }} />
        ) : isGuest ? (
          <Flex
            as="button"
            onClick={() => navigate("/signup")}
            align="center"
            justify="center"
            gap="0.4rem"
            flexShrink={0}
            h={{ base: "2.6rem", tablet: "2.55rem" }}
            px="1.4rem"
            rounded="full"
            bg="white"
            color={RIBBON_BG}
            fontWeight="700"
            fontSize={{ base: "0.85rem", tablet: "0.875rem" }}
            cursor="pointer"
            transition="all 0.2s ease"
            _hover={{ bg: "#e9f7ef", transform: "translateY(-1px)" }}
          >
            <UserPlus size={15} />
            Register now
          </Flex>
        ) : (
          <Flex
            direction="row"
            align="center"
            gap="0.55rem"
            flexShrink={0}
            w={{ base: "full", tablet: "auto" }}
          >
            <Flex
              as="button"
              onClick={handleCopyCode}
              align="center"
              justify="space-between"
              gap="0.5rem"
              flex={{ base: "1", tablet: "none" }}
              minW={0}
              h={{ base: "2.6rem", tablet: "2.55rem" }}
              px="1rem"
              rounded="full"
              bg="rgba(255,255,255,0.1)"
              border={`1px solid ${MINT}59`}
              cursor={referralCode ? "pointer" : "not-allowed"}
              opacity={referralCode ? 1 : 0.5}
              transition="all 0.18s ease"
              _hover={
                referralCode
                  ? { bg: "rgba(255,255,255,0.16)", borderColor: MINT }
                  : undefined
              }
            >
              <Text
                fontFamily="monospace"
                fontWeight="800"
                color="white"
                letterSpacing="0.08em"
                fontSize="0.85rem"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {copied ? "Copied!" : copyFailed ? "Copy failed" : referralCode ?? "------"}
              </Text>
              {copied ? (
                <Check size={14} color={MINT} />
              ) : (
                <Copy size={14} color="rgba(255,255,255,0.65)" />
              )}
            </Flex>

            <Flex
              as="button"
              onClick={handleShare}
              align="center"
              justify="center"
              gap="0.4rem"
              flex={{ base: "1", tablet: "none" }}
              minW={0}
              h={{ base: "2.6rem", tablet: "2.55rem" }}
              px={{ base: "1.15rem", tablet: "1.4rem" }}
              rounded="full"
              bg="white"
              color={RIBBON_BG}
              fontWeight="700"
              fontSize={{ base: "0.85rem", tablet: "0.875rem" }}
              whiteSpace="nowrap"
              cursor={referralCode ? "pointer" : "not-allowed"}
              opacity={referralCode ? 1 : 0.6}
              transition="all 0.2s ease"
              _hover={
                referralCode
                  ? { bg: "#e9f7ef", transform: "translateY(-1px)" }
                  : undefined
              }
            >
              <Share2 size={15} />
              Share link
            </Flex>
          </Flex>
        )}
      </Flex>
    </Box>
  );
}
